import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ReferralCard } from '@/components/invitations';
import { ProfileChecklist } from '@/components/ProfileChecklist';
import { PurchasesCard } from '@/components/PurchasesCard';
import { TeamCard } from '@/components/teams';
import { Button, ProgressBar } from '@/components/ui';
import { useLogout, useMe, useMyTeams, useProfile } from '@/lib/queries';

export function Me() {
  const { t } = useTranslation();
  const me = useMe();
  const logout = useLogout();
  const qc = useQueryClient();
  const teams = useMyTeams(Boolean(me.data));
  const profile = useProfile(Boolean(me.data));

  if (me.isLoading) return <p className="text-center text-ink-muted">…</p>;
  if (me.isError || !me.data) return <Navigate to="/login" replace />;

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-1">
        <h1 className="m-0 font-display text-title font-bold">
          {profile.data?.profile.firstName
            ? t('profile.greeting', { name: profile.data.profile.firstName })
            : t('me.title')}
        </h1>
        <p className="m-0 text-sm text-ink-muted">{me.data.email}</p>
        {profile.data && (
          <div className="mt-2.5 flex flex-col gap-1.5">
            <span className="text-helper font-semibold">
              {t('profile.progress', { percent: profile.data.completion.percent })}
            </span>
            <ProgressBar
              value={profile.data.completion.percent / 100}
              label={t('profile.progress', { percent: profile.data.completion.percent })}
            />
          </div>
        )}
      </header>

      <PurchasesCard />

      {profile.data && <ProfileChecklist data={profile.data} />}

      <ReferralCard />

      {teams.data?.teams.map((team) => (
        <TeamCard key={team.id} team={team} />
      ))}

      <Button
        variant="ghost"
        onClick={async () => {
          await logout.mutateAsync();
          await qc.invalidateQueries({ queryKey: ['me'] });
        }}
      >
        {t('me.logout')}
      </Button>
    </section>
  );
}
